/*  eslint-disable no-unused-vars  */
import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import { initConsoleGuard } from "./utils/consoleGuard";
import { installNetworkSpeedProbe } from "./utils/networkSpeed";
import { installSetAttributeGuard } from "./utils/setAttributeGuard";

// Installed before anything renders. Vue does not wrap its patch phase in a
// try/catch, so a single attribute value that cannot be coerced to a string
// aborts the mount and leaves a blank page; this downgrades that to one logged
// error naming the offending attribute. See utils/setAttributeGuard.js.
installSetAttributeGuard();

// Self-XSS deterrence: warn anyone who opens the DevTools console not to paste/run
// scripts (this is medical software). Warning only — right-click and DevTools
// shortcuts stay enabled so developers can freely open the inspect panel.
initConsoleGuard({ disableContextMenu: false, deterShortcuts: false });

// Installed here, not in the footer badge that displays it. The axios
// interceptors have to exist before the first view dispatches its data load,
// and draining the resource-timing buffer at this point captures the bundle
// download that just finished — so the badge has a real number on first paint
// instead of "--". Adds no requests of its own; see utils/networkSpeed.js.
installNetworkSpeedProbe();

import aikenistLogo from "@/assets/AikenistLogo.png";
import aikenistLogoW from "@/assets/aikenist-logo.png";

const setLogo = (key, asset) => {
  const path = asset.default || asset;
  const absoluteUrl = new URL(path, document.baseURI).href;
  document.documentElement.style.setProperty(key, `url("${absoluteUrl}")`);
};

setLogo("--aikenist-logo", aikenistLogo);
setLogo("--aikenist-logo-w", aikenistLogoW);

// ---------------------------------------------------------------------------
// Element-UI, registered per component instead of `Vue.use(ElementUI)`.
//
// element-ui publishes only a CommonJS build — its package.json has no `module`
// field and no `sideEffects` flag — so importing the package root defeats
// tree-shaking entirely and every one of its 90 components shipped in
// chunk-vendors, on every route including the login page. Cascader, Tree,
// Transfer, Slider, Calendar, Autocomplete and friends were all in the bundle
// despite this app never rendering one.
//
// The list below is generated from the templates: every `<el-*>` tag that
// appears anywhere in src/. tests/unit/elementComponentCoverage.spec.js
// re-derives that set from source and fails if a tag is ever used without being
// registered here, so adding a new element-ui component to a template cannot
// silently ship a broken screen.
//
// Registration stays GLOBAL rather than per-component-file on purpose: several
// views build element-ui nodes by name in render functions — ManagerList.vue
// does h("el-tooltip", ...) and six AutoActions views do h("el-popover", ...) —
// and name resolution only works against globally registered components.
//
// The full theme-chalk stylesheet is kept as-is. Splitting CSS per component is
// where on-demand loading usually goes wrong, and the JS is the part that costs
// parse time on a slow machine.
// ---------------------------------------------------------------------------
import "element-ui/lib/theme-chalk/index.css";
import "@/assets/tailwind.css";
import elementLocale from "element-ui/lib/locale";
import lang from "element-ui/lib/locale/lang/en";

import ElAlert from "element-ui/lib/alert";
import ElAside from "element-ui/lib/aside";
import ElAvatar from "element-ui/lib/avatar";
import ElBadge from "element-ui/lib/badge";
import ElButton from "element-ui/lib/button";
import ElCard from "element-ui/lib/card";
import ElCarousel from "element-ui/lib/carousel";
import ElCarouselItem from "element-ui/lib/carousel-item";
import ElCheckbox from "element-ui/lib/checkbox";
import ElCollapse from "element-ui/lib/collapse";
import ElCollapseItem from "element-ui/lib/collapse-item";
import ElColorPicker from "element-ui/lib/color-picker";
import ElContainer from "element-ui/lib/container";
import ElDatePicker from "element-ui/lib/date-picker";
import ElDialog from "element-ui/lib/dialog";
import ElDivider from "element-ui/lib/divider";
import ElDrawer from "element-ui/lib/drawer";
import ElDropdown from "element-ui/lib/dropdown";
import ElDropdownItem from "element-ui/lib/dropdown-item";
import ElDropdownMenu from "element-ui/lib/dropdown-menu";
import ElEmpty from "element-ui/lib/empty";
import ElFooter from "element-ui/lib/footer";
import ElForm from "element-ui/lib/form";
import ElFormItem from "element-ui/lib/form-item";
import ElIcon from "element-ui/lib/icon";
import ElInput from "element-ui/lib/input";
import ElInputNumber from "element-ui/lib/input-number";
import ElLink from "element-ui/lib/link";
import ElMain from "element-ui/lib/main";
import ElMenu from "element-ui/lib/menu";
import ElMenuItem from "element-ui/lib/menu-item";
import ElMenuItemGroup from "element-ui/lib/menu-item-group";
import ElOption from "element-ui/lib/option";
import ElOptionGroup from "element-ui/lib/option-group";
import ElPagination from "element-ui/lib/pagination";
import ElPopconfirm from "element-ui/lib/popconfirm";
import ElPopover from "element-ui/lib/popover";
import ElProgress from "element-ui/lib/progress";
import ElRadio from "element-ui/lib/radio";
import ElRadioButton from "element-ui/lib/radio-button";
import ElRadioGroup from "element-ui/lib/radio-group";
import ElRow from "element-ui/lib/row";
import ElSelect from "element-ui/lib/select";
import ElStep from "element-ui/lib/step";
import ElSteps from "element-ui/lib/steps";
import ElSubmenu from "element-ui/lib/submenu";
import ElSwitch from "element-ui/lib/switch";
import ElTabPane from "element-ui/lib/tab-pane";
import ElTable from "element-ui/lib/table";
import ElTableColumn from "element-ui/lib/table-column";
import ElTabs from "element-ui/lib/tabs";
import ElTag from "element-ui/lib/tag";
import ElTimePicker from "element-ui/lib/time-picker";
import ElTimeSelect from "element-ui/lib/time-select";
import ElTooltip from "element-ui/lib/tooltip";
import ElUpload from "element-ui/lib/upload";

// Registered globally by element-ui's own install(). Collapse and Submenu also
// register it locally, but keeping it here matches install() exactly.
import ElCollapseTransition from "element-ui/lib/transitions/collapse-transition";

// Directives and the imperative services ($message, $confirm, v-loading, ...).
import ElInfiniteScroll from "element-ui/lib/infinite-scroll";
import ElLoading from "element-ui/lib/loading";
import ElMessage from "element-ui/lib/message";
import ElMessageBox from "element-ui/lib/message-box";
import ElNotification from "element-ui/lib/notification";

import VueObserveVisibility from "vue-observe-visibility";

const elementComponents = [
  ElAlert,
  ElAside,
  ElAvatar,
  ElBadge,
  ElButton,
  ElCard,
  ElCarousel,
  ElCarouselItem,
  ElCheckbox,
  ElCollapse,
  ElCollapseItem,
  ElCollapseTransition,
  ElColorPicker,
  ElContainer,
  ElDatePicker,
  ElDialog,
  ElDivider,
  ElDrawer,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElEmpty,
  ElFooter,
  ElForm,
  ElFormItem,
  ElIcon,
  ElInput,
  ElInputNumber,
  ElLink,
  ElMain,
  ElMenu,
  ElMenuItem,
  ElMenuItemGroup,
  ElOption,
  ElOptionGroup,
  ElPagination,
  ElPopconfirm,
  ElPopover,
  ElProgress,
  ElRadio,
  ElRadioButton,
  ElRadioGroup,
  ElRow,
  ElSelect,
  ElStep,
  ElSteps,
  ElSubmenu,
  ElSwitch,
  ElTabPane,
  ElTable,
  ElTableColumn,
  ElTabs,
  ElTag,
  ElTimePicker,
  ElTimeSelect,
  ElTooltip,
  ElUpload,
];

Vue.use(VueObserveVisibility);

// Mirrors element-ui's own install(), minus the components this app never uses.
elementLocale.use(lang);

elementComponents.forEach((component) => {
  Vue.component(component.name, component);
});

Vue.use(ElInfiniteScroll);
Vue.use(ElLoading.directive);

Vue.prototype.$ELEMENT = { size: "", zIndex: 2000 };

Vue.prototype.$loading = ElLoading.service;
Vue.prototype.$msgbox = ElMessageBox;
Vue.prototype.$alert = ElMessageBox.alert;
Vue.prototype.$confirm = ElMessageBox.confirm;
Vue.prototype.$prompt = ElMessageBox.prompt;
Vue.prototype.$notify = ElNotification;
Vue.prototype.$message = ElMessage;

const DialogComponent = ElDialog.default || ElDialog;
if (
  DialogComponent &&
  DialogComponent.props &&
  DialogComponent.props.destroyOnClose
) {
  DialogComponent.props.destroyOnClose.default = true;
}

// Must come after the loop above so this override wins over the plain
// registration of ElTooltip.
Vue.component("el-tooltip", {
  extends: ElTooltip,
  props: {
    openDelay: {
      type: Number,
      default: 500,
    },
  },
});
Vue.config.productionTip = false;

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount("#app");
